export const BRAND = {
  name: "AG Public Adjusting",
  legalName: "AG Public Adjusting",
  tagline: "Licensed Public Adjuster Firm",
  logoWhite: "/images/logos/ag-white.webp",
  logoDark: "/images/logos/ag-black.webp",
  logoGold: "/images/logos/ag-gold.webp",
  phoneDisplay: "(407) 201-7699",
  phoneHref: "tel:+14072017699",
  email: "Claims@AGPublicAdjusting.com",
  emailHref: "mailto:Claims@AGPublicAdjusting.com",
  domain: "agpublicadjusting.com",
  licenseNumber: "Licensed Florida Public Adjuster Firm",
  address: {
    street: "30 Broadway",
    city: "Kissimmee",
    state: "FL",
    zip: "34741",
    country: "United States",
    full: "30 Broadway, Kissimmee, FL 34741",
    mapsQuery: "30+Broadway,+Kissimmee,+FL+34741",
    mapsHref: "https://www.google.com/maps/search/?api=1&query=30+Broadway,+Kissimmee,+FL+34741",
  },
  hours: [
    { day: "Monday – Friday", time: "9:00 AM – 5:00 PM" },
    { day: "Saturday", time: "By Appointment Only" },
    { day: "Sunday", time: "By Appointment Only" },
  ],
  credit: {
    label: "Powered by Eternity MP",
    company: "Eternity MP",
    href: "https://www.eternitymp.com",
  },
};

// Self-hosted under /app/frontend/public/images/ — stable, version-controlled with deploy.
const img = (name) => `/images/${name}.jpg`;

export const IMAGES = {
  // Hero / atmospheric
  hero: img("hero"),
  florida_aerial: img("florida_aerial"),
  // Storm / hurricane damage
  storm_damage: img("storm_damage"),
  storm_aftermath: img("hurricane"),       // fallback — original URL was dead
  hurricane: img("hurricane"),
  // Roof / inspection
  roof_inspection: img("roof_inspection"),
  inspector: img("inspector"),
  drone: img("drone"),
  // Water / fire damage interiors
  water_damage: img("water_damage"),
  fire_damage: img("storm"),               // fallback — original URL was dead
  // Documentation / tools / blueprints
  blueprints: img("blueprints"),
  documentation: img("documentation"),
  measure: img("measure"),
  // Commercial / condo / property
  commercial: img("commercial"),
  condo: img("condo"),
  florida_home: img("florida_home"),
  // Human / family / recovery
  family_home: img("family_home"),
  meeting: img("meeting"),
  consultation: img("consultation"),
  // Architecture
  architecture: img("architecture"),
  // Legacy
  property: img("property"),
  inspection: img("inspection"),
  storm: img("storm"),
  florida: img("florida"),
};

// Premium AI-generated cinematic Hero scenes — one per service (12 total).
// Each scene carries its own title/subtitle/accent — the Hero becomes a chaptered
// cinematic sequence, not a slideshow.
const heroImg = (name) => `/images/hero/${name}.png`;

export const HERO_SCENES = [
  {
    slug: "hurricane",
    chapter: "Chapter 01",
    title: "Hurricane Claims",
    subtitle: "Protecting Florida property owners after catastrophic storm damage.",
    image: heroImg("hurricane"),
    fx: "rain",
    camera: "driftDiagTL",
    transition: "stormFlash",
    accent: "blue",
    coords: "FLORIDA · NATIONWIDE",
    status: "FIELD STATUS // CATEGORY 4 INTERCEPT",
    locale: "CATASTROPHIC LOSS",
    situation: "CAT-4 INTERCEPT",
    renderMode: "video",
    video: "/videos/hero/hurricane.mp4"
  },
  {
    slug: "water_damage",
    chapter: "Chapter 02",
    title: "Water Damage Claims",
    subtitle: "When water destroys what you've built — we recover what it took.",
    image: heroImg("water_damage"),
    fx: "ripple",
    camera: "pushIn",
    transition: "waterRipple",
    accent: "blue",
    coords: "PROPERTY DAMAGE",
    status: "FIELD STATUS // FLOOD RECOVERY",
    locale: "RESIDENTIAL CLAIMS",
    situation: "FLOOD RECOVERY",
    renderMode: "image"
  },
  {
    slug: "fire_damage",
    chapter: "Chapter 03",
    title: "Fire & Smoke Claims",
    subtitle: "After the smoke clears, we make insurers answer for every loss.",
    image: heroImg("fire_damage"),
    fx: "ember",
    camera: "pushOut",
    transition: "smokeWipe",
    accent: "gold",
    coords: "CATASTROPHIC LOSS",
    status: "FIELD STATUS // POST-FIRE INTAKE",
    locale: "PROPERTY DAMAGE",
    situation: "POST-FIRE INTAKE",
    renderMode: "video"
  },
  {
    slug: "roof_damage",
    chapter: "Chapter 04",
    title: "Roof Damage Claims",
    subtitle: "Documented from above. Defended on your behalf.",
    image: heroImg("roof_damage"),
    fx: "scan",
    camera: "parallaxDown",
    transition: "blueprintScan",
    accent: "blue",
    coords: "RESIDENTIAL · COMMERCIAL",
    status: "FIELD STATUS // AERIAL SCAN",
    locale: "PROPERTY INSPECTION",
    situation: "AERIAL SCAN",
    renderMode: "image"
  },
  {
    slug: "mold_damage",
    chapter: "Chapter 05",
    title: "Mold Damage Claims",
    subtitle: "Hidden damage demands forensic representation.",
    image: heroImg("mold_damage"),
    fx: "flashlight",
    camera: "driftRight",
    transition: "lensFlare",
    accent: "gold",
    coords: "FORENSIC ASSESSMENT",
    status: "FIELD STATUS // FORENSIC SWEEP",
    locale: "POLICYHOLDER REPRESENTATION",
    situation: "FORENSIC SWEEP",
    renderMode: "image"
  },
  {
    slug: "storm_damage",
    chapter: "Chapter 06",
    title: "Storm Damage Claims",
    subtitle: "Storms don't negotiate. Neither do we.",
    image: heroImg("storm_damage"),
    fx: "rain",
    camera: "driftLeft",
    transition: "smokeWipe",
    accent: "blue",
    coords: "FLORIDA · NATIONWIDE",
    status: "FIELD STATUS // POST-STORM SURVEY",
    locale: "CATASTROPHIC LOSS",
    situation: "POST-STORM SURVEY",
    renderMode: "image"
  },
  {
    slug: "pipe_burst",
    chapter: "Chapter 07",
    title: "Pipe Burst Claims",
    subtitle: "A single failure can drown years of equity. We recover it.",
    image: heroImg("pipe_burst"),
    fx: "ripple",
    camera: "pushIn",
    transition: "waterRipple",
    accent: "blue",
    coords: "PROPERTY DAMAGE",
    status: "FIELD STATUS // ACTIVE LEAK",
    locale: "RESIDENTIAL CLAIMS",
    situation: "ACTIVE LEAK",
    renderMode: "image"
  },
  {
    slug: "commercial",
    chapter: "Chapter 08",
    title: "Commercial Property Claims",
    subtitle: "Complex commercial losses, methodically rebuilt into recoveries.",
    image: heroImg("commercial"),
    fx: "scan",
    camera: "driftDiagBR",
    transition: "blueprintScan",
    accent: "blue",
    coords: "INSTITUTIONAL ASSESSMENT",
    status: "FIELD STATUS // STRUCTURAL ASSESSMENT",
    locale: "COMMERCIAL CLAIMS",
    situation: "STRUCTURAL ASSESSMENT",
    renderMode: "image"
  },
  {
    slug: "business_interruption",
    chapter: "Chapter 09",
    title: "Business Interruption",
    subtitle: "Every closed day is a measurable loss. We prove it.",
    image: heroImg("business_interruption"),
    fx: "scan",
    camera: "pushIn",
    transition: "glassDistort",
    accent: "gold",
    coords: "LOSS QUANTIFICATION",
    status: "FIELD STATUS // LOSS QUANTIFICATION",
    locale: "COMMERCIAL CLAIMS",
    situation: "LOSS QUANTIFICATION",
    renderMode: "image"
  },
  {
    slug: "denied_claim",
    chapter: "Chapter 10",
    title: "Denied Claims",
    subtitle: "Denied is not the end. It's where our work begins.",
    image: heroImg("denied_claim"),
    fx: "scan",
    camera: "orbitR",
    transition: "lensFlare",
    accent: "gold",
    coords: "CLAIM DISPUTE",
    status: "FIELD STATUS // POLICY DISPUTE",
    locale: "POLICYHOLDER REPRESENTATION",
    situation: "POLICY DISPUTE",
    renderMode: "image"
  },
  {
    slug: "underpaid_claim",
    chapter: "Chapter 11",
    title: "Underpaid Claims",
    subtitle: "Insurers underpay. We re-open, re-document, re-negotiate.",
    image: heroImg("underpaid_claim"),
    fx: "scan",
    camera: "driftRight",
    transition: "glassDistort",
    accent: "gold",
    coords: "CLAIM RECOVERY",
    status: "FIELD STATUS // RECALCULATION",
    locale: "POLICYHOLDER REPRESENTATION",
    situation: "RECALCULATION",
    renderMode: "image"
  },
  {
    slug: "condo_hoa",
    chapter: "Chapter 12",
    title: "Condo & HOA Claims",
    subtitle: "Association-level claims demand institutional representation.",
    image: heroImg("condo_hoa"),
    fx: "scan",
    camera: "pushOut",
    transition: "blueprintScan",
    accent: "blue",
    coords: "BOARD-LEVEL CLAIMS",
    status: "FIELD STATUS // BOARD REPRESENTATION",
    locale: "ASSOCIATION REPRESENTATION",
    situation: "BOARD REPRESENTATION",
    renderMode: "image"
  },
];
